import React, { useState, useEffect, useMemo } from 'react';
import { videoSessionAPI } from '../../services/api';
import { X, Calendar, Clock, User, Lock, Unlock, Copy, Check, Video } from 'lucide-react';
import { getMeetLink } from '../../utils/videoHelper';
import {
  getUserTimezone,
  getDateTimeForInput,
  convertLocalToUTC,
  parseISODate
} from '../../utils/dateUtils';
import { addMinutes, differenceInMinutes } from 'date-fns';

const VideoSessionModal = ({ partnerId, users, selectedSlot, session, onClose, onSave, therapyFees = [] }) => {

  const [formData, setFormData] = useState({
    user_id: '',
    therapy_type: '',
    session_date: '',
    end_date: '',
    duration_minutes: 60,
    password_enabled: true,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdSession, setCreatedSession] = useState(null);
  const [copied, setCopied] = useState(false);

  // Get selected therapy data
  const selectedTherapyData = useMemo(() => {
    if (!formData.therapy_type || !therapyFees) return null;
    return therapyFees.find(t => t.therapy_type === formData.therapy_type);
  }, [formData.therapy_type, therapyFees]);

  // Auto-calculate end time based on selected therapy type's duration
  const calculatedEndTime = useMemo(() => {
    if (!formData.session_date || !selectedTherapyData?.duration) return '';

    const startDate = new Date(formData.session_date);
    if (isNaN(startDate.getTime())) return '';

    const endDate = addMinutes(startDate, selectedTherapyData.duration);
    return getDateTimeForInput(endDate);
  }, [formData.session_date, selectedTherapyData]);

  useEffect(() => {
    if (session) {
      // Editing existing session - Convert UTC back to user's local timezone
      setFormData({
        user_id: session.user_id,
        therapy_type: session.therapy_type || '',
        session_date: getDateTimeForInput(session.session_date),
        end_date: getDateTimeForInput(session.end_date),
        duration_minutes: session.duration_minutes,
        password_enabled: session.password_enabled,
        notes: session.notes || ''
      });
    } else if (selectedSlot) {
      // Creating new session from calendar slot
      const start = new Date(selectedSlot.start);
      const end = new Date(selectedSlot.end);

      const duration = differenceInMinutes(end, start);

      setFormData(prev => ({
        ...prev,
        session_date: getDateTimeForInput(start),
        end_date: getDateTimeForInput(end),
        duration_minutes: duration > 0 ? duration : 60
      }));
    }
  }, [session, selectedSlot]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Auto-update end_date and duration_minutes when calculatedEndTime changes
  useEffect(() => {
    if (calculatedEndTime && selectedTherapyData?.duration) {
      setFormData(prev => ({
        ...prev,
        end_date: calculatedEndTime,
        duration_minutes: selectedTherapyData.duration
      }));
    }
  }, [calculatedEndTime, selectedTherapyData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.user_id) {
        setError('Please select a client');
        setLoading(false);
        return;
      }

      if (!formData.therapy_type) {
        setError('Please select a therapy type. Set up therapy types in the Availability tab under "Set Session Fee" first.');
        setLoading(false);
        return;
      }

      if (!calculatedEndTime) {
        setError('Cannot calculate end time. Please ensure the therapy type has duration configured in \'Set Session Fee\' settings in Availability tab.');
        setLoading(false);
        return;
      }

      // Get user's timezone
      const userTimezone = getUserTimezone();

      // Convert local datetime to UTC for storage
      const localSessionDate = convertLocalToUTC(new Date(formData.session_date));
      const localEndDate = convertLocalToUTC(new Date(formData.end_date));

      // Generate title from therapy type
      const title = formData.therapy_type
        ? `${formData.therapy_type.charAt(0).toUpperCase() + formData.therapy_type.slice(1)} Therapy Session`
        : 'Video Session';

      const sessionData = {
        partner_id: partnerId,
        user_id: parseInt(formData.user_id),
        title: title,
        therapy_type: formData.therapy_type,
        session_date: localSessionDate.toISOString(),
        end_date: localEndDate.toISOString(),
        duration_minutes: parseInt(formData.duration_minutes),
        password_enabled: formData.password_enabled,
        notes: formData.notes,
        timezone: userTimezone
      };

      if (session) {
        // Update existing session
        await videoSessionAPI.update(session.id, sessionData);
        onSave();
      } else {
        // Create new session
        const response = await videoSessionAPI.create(sessionData);
        setCreatedSession(response.data.session);
      }
    } catch (err) {
      console.error('Save video session error:', err);
      setError(err.response?.data?.error || 'Failed to save video session');
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (createdSession) {
      const meetingUrl = createdSession.meet_link || getMeetLink(createdSession);
      const therapyTypeLabel = createdSession.therapy_type
        ? `${createdSession.therapy_type.charAt(0).toUpperCase() + createdSession.therapy_type.slice(1)} Therapy`
        : 'Video Session';
      const shareText = `Video Session: ${therapyTypeLabel}\n` +
        `Date: ${new Date(createdSession.session_date).toLocaleString()}\n` +
        `Duration: ${createdSession.duration_minutes} minutes\n` +
        `Meeting Link: ${meetingUrl}` +
        (createdSession.password_enabled ? `\nPassword: ${createdSession.plain_password}` : '');

      navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFinish = () => {
    onSave();
    onClose();
  };

  if (createdSession) {
    const meetingUrl = createdSession.meet_link || getMeetLink(createdSession);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Video Session Created!</h2>
              </div>
              <button onClick={handleFinish} className="text-gray-400 dark:text-white hover:text-gray-600 dark:hover:text-gray-300">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-primary-50 dark:bg-primary-900 border border-primary-200 dark:border-primary-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Session Details</h3>
                <div className="space-y-2 text-sm">
                  <p className="dark:text-gray-300"><span className="font-medium dark:text-white">Therapy Type:</span> {createdSession.therapy_type ? `${createdSession.therapy_type.charAt(0).toUpperCase() + createdSession.therapy_type.slice(1)} Therapy` : 'N/A'}</p>
                  <p className="dark:text-gray-300"><span className="font-medium dark:text-white">Date:</span> {new Date(createdSession.session_date).toLocaleString()}</p>
                  <p className="dark:text-gray-300"><span className="font-medium dark:text-white">Duration:</span> {createdSession.duration_minutes} minutes</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Meeting Link</h3>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={meetingUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {createdSession.password_enabled && (
                <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Lock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Session Password</h3>
                  </div>
                  <p className="text-2xl font-mono font-bold text-gray-900 dark:text-white mb-2">
                    {createdSession.plain_password}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Share this password with your client. They will need it to join the session.
                  </p>
                </div>
              )}

              <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Note:</strong> The session will automatically appear in your calendar.
                  You can copy the meeting link and password to share with your client.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handleCopyLink}
                  className="btn btn-secondary flex items-center space-x-2"
                >
                  <Copy className="h-4 w-4" />
                  <span>Copy All Details</span>
                </button>
                <button onClick={handleFinish} className="btn btn-primary">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                <Video className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {session ? 'Edit Video Session' : 'Schedule Video Session'}
              </h2>
            </div>
            <button onClick={onClose} className="text-gray-400 dark:text-white hover:text-gray-600 dark:hover:text-gray-300">
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <User className="inline h-4 w-4 mr-1 text-gray-700 dark:text-white" />
                Select Client *
              </label>
              <select
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                required
                disabled={!!session}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Choose a client...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email || user.contact})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Therapy Type *
              </label>
              <select
                name="therapy_type"
                value={formData.therapy_type}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Therapy Type</option>
                {therapyFees.map(t => (
                  <option key={t.therapy_type} value={t.therapy_type}>
                    {t.therapy_type.charAt(0).toUpperCase() + t.therapy_type.slice(1)} Therapy ({t.duration} min)
                  </option>
                ))}
              </select>
              {therapyFees.length === 0 && (
                <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                  No therapy types configured. Please set up therapy types in the Availability tab under "Set Session Fee" first.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="inline h-4 w-4 mr-1 text-gray-700 dark:text-white" />
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  name="session_date"
                  value={formData.session_date}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Clock className="inline h-4 w-4 mr-1 text-gray-700 dark:text-white" />
                  End Time (auto-calculated)
                </label>
                <input
                  type="datetime-local"
                  value={calculatedEndTime}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
                {selectedTherapyData && (
                  <p className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs px-2 py-0.5 rounded mt-1">
                    Duration: {selectedTherapyData.duration} minutes
                  </p>
                )}
                {formData.therapy_type && formData.session_date && !calculatedEndTime && (
                  <p className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs px-2 py-0.5 rounded mt-1">
                    Error: Unable to calculate end time. Please ensure the therapy type has duration configured in 'Set Session Fee' settings in Availability tab.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="password_enabled"
                  checked={formData.password_enabled}
                  onChange={handleChange}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 dark:bg-gray-800"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    {formData.password_enabled ? (
                      <Lock className="h-5 w-5 text-primary-600" />
                    ) : (
                      <Unlock className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">Password Protection</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {formData.password_enabled
                      ? 'A password will be generated to secure this session'
                      : 'Anyone with the link can join this session'}
                  </p>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Add any additional notes about this session..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : session ? 'Update Session' : 'Create Session'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VideoSessionModal;

